<script setup lang="ts">
import type { InputPrimaryEvent, NativeSelectOption } from '@shared/ui/fields'
import type { ActivityEditorProps } from '../../../model/assessments/activity-editor.hook'

import { AppText } from '@shared/ui/typography'
import { InputPrimary, NativeSelect } from '@shared/ui/fields'
import { BtnToggle } from '@shared/ui/buttons'

import { useActivityEditor } from '../../../model/assessments/activity-editor.hook'
import { useActivityRubric } from '../../../model/assessments/activity-rubric.hook'
import ContentBlockList from './ContentBlockList.vue'
import ActivityRubric from './rubrics/ActivityRubric.vue'
import StandardSelect from './StandardSelect.vue'

defineOptions({
  name: 'ActivityEditor',
})

const props = defineProps<ActivityEditorProps>()

const {
  refActivity,
  refOwner,
  refName,
  refPoints,
  refInteractionType,
  refInteractionTypeOptions,
  refInteractionEditor,
  setName,
  setPoints,
  setInteractionType,
} = useActivityEditor({
  screenId: () => props.screenId,
  activityId: () => props.activityId,
})

const {
  refIsAutoGraded,
  setAutoGraded,
} = useActivityRubric({
  screenId: () => props.screenId,
  activityId: () => props.activityId,
})
</script>

<template>
  <div
    v-if="refActivity"
    class="activity-editor"
  >
    <!-- No heading here: the list row above this card is the activity's title (position, name
         and interaction type), so repeating "Activity 1" would be the same words twice. -->

    <!-- What the question is FOR, in the author's words. The tutor's review lists this instead
         of the prompt, which is the kid's own text and reads as a sentence, not a handle. The
         kid never sees it. -->
    <InputPrimary
      name="name"
      label="Question name"
      optional-label="optional"
      :value="refName"
      placeholder="e.g. Multi-digit addition"
      @input="(event: InputPrimaryEvent) => setName(String(event.value ?? ''))"
    />

    <!-- The very same owner-agnostic `ContentBlockList` the screen uses (T10) — an activity's
         `content` is the prompt the interaction answers. -->
    <ContentBlockList
      :owner="refOwner"
      label="Activity content"
    />

    <AppText name="body2-medium">
      Interaction
    </AppText>

    <NativeSelect
      name="interactionType"
      :options="refInteractionTypeOptions"
      :value="refInteractionType"
      @change="(option: NativeSelectOption) => setInteractionType(String(option.value ?? ''))"
    />

    <!-- Dispatched through `INTERACTION_EDITORS`, never a `v-if` chain on the type: the registry
         is what makes a seventh interaction type a compile error instead of a blank panel. -->
    <component
      :is="refInteractionEditor"
      v-if="refInteractionEditor && refActivity.interaction"
      :screen-id="screenId"
      :activity-id="activityId"
      :interaction="refActivity.interaction"
    />

    <!-- WHO MARKS THIS QUESTION, and last because it is about what happens AFTER the kid answers
         the interaction above. Auto-graded and tutor-reviewed are the two sides of one fact —
         whether the question carries a rubric — so exactly one of Points and Rubric follows the
         switch: points score a machine-marked question, a rubric's bands score a reviewed one,
         and a question holding both would be scored twice by two different numbers. -->
    <AppText name="body2-medium">
      Grading
    </AppText>

    <div class="activity-editor__toggle">
      <BtnToggle
        :checked="refIsAutoGraded"
        @click="(value: boolean) => setAutoGraded(value)"
      />
      <AppText name="body2">
        Auto-graded
      </AppText>
    </div>

    <!-- AUTO-GRADED: what the machine awards, and what the question is aligned to. A quarter for
         a number that is at most three digits, the rest for the picker and its chips. The
         standards live on the ACTIVITY here, because there is no rubric to hang them on. -->
    <div
      v-if="refIsAutoGraded"
      class="activity-editor__row"
    >
      <!-- An emptied points field passes `null`, which DELETES the optional key. It never passes
           `0` — that is a real authored value meaning "worth nothing". -->
      <InputPrimary
        name="points"
        type="number"
        label="Points"
        optional-label="optional"
        class-name="activity-editor__points"
        :value="refPoints"
        placeholder="How much this question is worth"
        @input="(event: InputPrimaryEvent) => setPoints(event.value)"
      />

      <!-- One field, backed by the DB catalogue and filtered to this assessment's grade. It
           replaced two free-text inputs, which asked an author to type both a code and its 150-
           character statement by hand — and produced the cluster-letter spelling (`4.NBT.B.4`)
           that the catalogue does not contain. -->
      <StandardSelect
        class="activity-editor__standards"
        :owner="{ screenId, activityId }"
      />
    </div>

    <!-- ONE rubric per question, authored here. Both halves of decision B8 — the catalog entry
         in `data.rubrics` and the id in `Activity.rubrics` — are written together by
         `useActivityRubric`, so there is no document-level catalog editor any more. The other
         half of the switch: auto-graded IS the state of having no rubrics at all, and each
         rubric here carries its own standards. -->
    <ActivityRubric
      v-if="!refIsAutoGraded"
      :screen-id="screenId"
      :activity-id="activityId"
    />
  </div>
</template>

<style scoped lang="scss" src="./ActivityEditor.scss" />
